<script setup lang="ts">
import { computed } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useI18n, type I18nKey } from '@/i18n'

const docStore = useDocumentStore()
const { t } = useI18n()
const entries = computed(() => {
  const { undo, redo } = docStore.historyEntries()
  return { undo: [...undo], redo: [...redo].reverse() }
})
function label(key: string, params?: Record<string, string | number>): string {
  return t(key as I18nKey, params)
}
function jumpUndo(index: number) {
  // index 番目 (0 = 最古) の直前まで戻す
  const n = entries.value.undo.length - index
  for (let i = 0; i < n; i++) docStore.undo()
}
function jumpRedo(index: number) {
  for (let i = 0; i <= index; i++) docStore.redo()
}
</script>

<template>
  <div class="panel">
    <div class="panel-title row"><span>{{ t('panel.history') }}</span><span class="spacer" /><button class="icon ghost" :title="t('snapshot.take')" :disabled="!docStore.hasDocument" @click="docStore.takeSnapshot()">📷</button></div>
    <div class="list">
      <div v-for="s in docStore.snapshots" :key="s.id" class="item snap" @click="docStore.restoreSnapshot(s.id)">
        <span>📷 {{ s.name }}</span><span class="spacer" /><button class="ghost tiny" :title="t('snapshot.delete')" @click.stop="docStore.deleteSnapshot(s.id)">×</button>
      </div>
      <div v-for="(e, i) in entries.undo" :key="e.id" class="item" :class="{ current: i === entries.undo.length - 1 }" @click="i === entries.undo.length - 1 ? undefined : jumpUndo(i + 1)">
        {{ label(e.label.key, e.label.params) }}
      </div>
      <div v-for="(e, i) in entries.redo" :key="e.id" class="item redo" @click="jumpRedo(i)">
        {{ label(e.label.key, e.label.params) }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}
.list {
  overflow: auto;
  flex: 1;
}
.item {
  padding: 3px 10px;
  border-bottom: 1px solid var(--line-weak);
  cursor: default;
}
.item:hover {
  background: var(--bg-hover);
}
.item.current {
  background: var(--accent-dim);
}
.item.redo {
  color: var(--fg-3);
}
.snap {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--accent);
}
.tiny {
  width: 20px;
  height: 18px;
  padding: 0;
}
</style>
